import React from 'react'
import { BtnV2, l } from '@manychat/manyui'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { useGoalEditorContext } from 'apps/aiAutomation/components/EngagementGoalEditor/lib/context'
import { GoalEditorMode } from 'apps/aiAutomation/components/EngagementGoalEditor/lib/types'
import { useIsIgDisconnected } from 'apps/aiAutomation/hooks/useIsIgDisconnected'
import { AiAutomationAnalytics } from 'apps/aiAutomation/lib/analytics'
import { AiAlwaysOnAutomationsSelectors } from 'apps/aiAutomation/store/core'
import {
  AiAutomationEngagementGoalActions,
  AiAutomationEngagementGoalSelectors,
} from 'apps/aiAutomation/store/engagementGoal'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import { EngagementGoal, GoalStatus } from 'shared/api/requests/aiEngagementGoal/schemas'

interface StatusButtonProps {
  goal: EngagementGoal
}

export const StatusButton = ({ goal }: StatusButtonProps) => {
  const dispatch = useAppDispatch()

  const { mode, changeMode, editorRef, save } = useGoalEditorContext(goal.type)

  const isUpdating = useAppSelector((state) =>
    AiAutomationEngagementGoalSelectors.getIsItemUpdating(state, goal.id),
  )
  const activeGoalsLimitReached = useAppSelector(
    AiAutomationEngagementGoalSelectors.getIsActiveGoalLimitReached,
  )
  const isAnyAutomationPublished = useAppSelector(
    (state) =>
      AiAlwaysOnAutomationsSelectors.getFaqScenarioStatus(state) ||
      AiAlwaysOnAutomationsSelectors.getIsSocialEngagementPublished(state),
  )

  const isIgDisconnected = useIsIgDisconnected()
  const isPublished = goal.status === GoalStatus.ACTIVE

  const handleEdit = () => {
    changeMode(GoalEditorMode.EDIT)
  }

  const handleSave = async () => {
    if (!editorRef?.current) {
      return
    }

    const result = await editorRef.current.submit()

    if (!result.isValid) {
      return
    }

    save(result.data)

    dispatch(AiAutomationLayoutActions.confirmChanged({ show: false }))
    if (isPublished) {
      changeMode(GoalEditorMode.READ)
    }
  }

  const handleStop = () => {
    dispatch(
      AiAutomationEngagementGoalActions.updateStatusFx({
        id: goal.id,
        status: GoalStatus.STOPPED,
      }),
    )

    AiAutomationAnalytics.logGoalStatusChanged({ id: goal.id, is_published: false })
  }

  const handleGoLive = async () => {
    if (!editorRef?.current) {
      return
    }

    const result = await editorRef.current.submit()

    if (!result.isValid) {
      return
    }

    save({ ...result.data, status: GoalStatus.ACTIVE }, true)

    dispatch(AiAutomationLayoutActions.confirmChanged({ show: false }))
    changeMode(GoalEditorMode.READ)
  }

  return (
    <>
      {mode === GoalEditorMode.READ && (
        <BtnV2
          label={l.translate('Edit')}
          ariaLabel={l.translate('Edit')}
          variant="default"
          loading={isUpdating}
          disabled={isIgDisconnected}
          onClick={handleEdit}
        />
      )}

      {(mode === GoalEditorMode.EDIT || mode === GoalEditorMode.SAVING_BLOCKED) && (
        <BtnV2
          label={isPublished ? l.translate('Update') : l.translate('Save')}
          ariaLabel={isPublished ? l.translate('Update') : l.translate('Save')}
          variant={isPublished ? 'primary' : 'default'}
          loading={isUpdating}
          disabled={mode === GoalEditorMode.SAVING_BLOCKED || isIgDisconnected}
          onClick={handleSave}
        />
      )}

      {isPublished && mode === GoalEditorMode.READ && (
        <BtnV2
          className="m-l-xs"
          label={l.translate('Stop')}
          ariaLabel={l.translate('Stop')}
          variant="danger-light"
          loading={isUpdating}
          disabled={isIgDisconnected}
          onClick={handleStop}
        />
      )}

      {!isPublished && isAnyAutomationPublished && !activeGoalsLimitReached && (
        <BtnV2
          className="m-l-xs"
          label={l.translate('Go Live')}
          ariaLabel={l.translate('Go Live')}
          variant="primary"
          loading={isUpdating}
          disabled={mode === GoalEditorMode.SAVING_BLOCKED || isIgDisconnected}
          onClick={handleGoLive}
        />
      )}
    </>
  )
}
